import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Login, Logout, PlayArrow, Stop, Description, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { api } from '../utils/api';
import { Task, User } from '../types';
import { formatDateToServerTimezone, formatTime } from '../utils/dateUtils';
import { areLocationsWithinThreshold, formatDistance, calculateDistance } from '../utils/locationUtils';
import TaskReportDialog from '../components/TaskReportDialog';
import { useSocket } from '../hooks/useSocket';

interface WorkingLogEntry {
  id: number;
  checkin: string | null;
  checkout: string | null;
  in_position?: string | null;
  out_position?: string | null;
  created_at?: string | null;
}

interface TodayStatus {
  exists: boolean;
  entries?: WorkingLogEntry[];
  checkin: string | null;
  checkout: string | null;
  in_position?: string | null;
  out_position?: string | null;
}

export default function WorkingLog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket, isConnected } = useSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [todayStatus, setTodayStatus] = useState<TodayStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [tasks, setTasks] = useState<(Task & { workLog?: { id: number; startTime: string | null; endTime: string | null } | null })[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [startingTask, setStartingTask] = useState<number | null>(null);
  const [stoppingTask, setStoppingTask] = useState<number | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [taskToStop, setTaskToStop] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [, setCurrentUser] = useState<User | null>(null);
  const [taskWorkLogs, setTaskWorkLogs] = useState<any[]>([]);
  const [loadingTaskWorkLogs, setLoadingTaskWorkLogs] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [taskToReport, setTaskToReport] = useState<Task | null>(null);
  const [taskHasReport, setTaskHasReport] = useState<Set<number>>(new Set());

  // Determine button states based on last entry's checkout status
  // START WORK: can only be pressed if last entry has checkout (or no entries exist)
  // STOP WORK: can only be pressed if last entry has checkin but no checkout
  const getLastEntry = () => {
    if (todayStatus?.entries && todayStatus.entries.length > 0) {
      return todayStatus.entries[0]; // First entry is most recent (ordered by desc)
    }
    return null;
  };

  const lastEntry = getLastEntry();
  const canCheckIn = !lastEntry || !!lastEntry.checkout; // Can check in if no entry or last entry has checkout
  const canCheckOut = lastEntry && !!lastEntry.checkin && !lastEntry.checkout; // Can check out if last entry has checkin but no checkout

  // Get current position as string (for checkin/checkout)
  const getCurrentPosition = (): Promise<string | null> => {
    return new Promise((resolve) => {
      console.log('📍 [Geolocation] Starting getCurrentPosition...');
      console.log('📍 [Geolocation] navigator.geolocation available:', !!navigator.geolocation);
      console.log('📍 [Geolocation] Current protocol:', window.location.protocol);
      console.log('📍 [Geolocation] Current hostname:', window.location.hostname);
      console.log('📍 [Geolocation] Is localhost:', window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      if (!navigator.geolocation) {
        console.error('📍 [Geolocation] navigator.geolocation is not available');
        resolve(null);
        return;
      }

      console.log('📍 [Geolocation] Calling getCurrentPosition with options:', {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });

      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('📍 [Geolocation] Success! Position received:', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date(position.timestamp).toISOString(),
          });
          const { latitude, longitude } = position.coords;
          const positionString = `${latitude}, ${longitude}`;
          console.log('📍 [Geolocation] Resolving with position:', positionString);
          resolve(positionString);
        },
        (error) => {
          // Handle geolocation errors gracefully
          // Error code 1 = PERMISSION_DENIED (including secure origin requirement)
          // Error code 2 = POSITION_UNAVAILABLE
          // Error code 3 = TIMEOUT
          console.error('📍 [Geolocation] Error occurred:', {
            code: error.code,
            message: error.message,
            codeDescription: 
              error.code === 1 ? 'PERMISSION_DENIED' :
              error.code === 2 ? 'POSITION_UNAVAILABLE' :
              error.code === 3 ? 'TIMEOUT' : 'UNKNOWN',
            protocol: window.location.protocol,
            hostname: window.location.hostname,
            isSecure: window.location.protocol === 'https:',
            isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
          });
          
          if (error.code === 1) {
            console.warn('📍 [Geolocation] Permission denied. This can happen if:');
            console.warn('   - User denied permission');
            console.warn('   - Site is not served over HTTPS (except localhost)');
            console.warn('   - Browser security settings block geolocation');
          } else if (error.code === 2) {
            console.warn('📍 [Geolocation] Position unavailable - GPS/network location not available');
          } else if (error.code === 3) {
            console.warn('📍 [Geolocation] Timeout - Location request took too long');
          }
          
          // Return null instead of a string to indicate no location
          console.log('📍 [Geolocation] Resolving with null due to error');
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  // Get current position as coordinates object (for location comparison)
  const getCurrentPositionCoords = (): Promise<{ latitude: number; longitude: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error('📍 [Geolocation] Error getting position:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  // Load working log status for selected date
  const loadData = async (date?: Date) => {
    try {
      setLoading(true);
      const dateToLoad = date || selectedDate;
      const dateStr = formatDateToServerTimezone(dateToLoad).split('T')[0]; // Format as YYYY-MM-DD
      const todayResponse = await api.get(`/working-log/today?date=${dateStr}`);
      setTodayStatus(todayResponse.data);
    } catch (err: any) {
      console.error('Error loading working log:', err);
      toast.error(err.response?.data?.message || 'Failed to load working log data');
    } finally {
      setLoading(false);
    }
  };

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await api.get('/users/profile/me');
        const userData = response.data;
        setCurrentUserId(userData.id);
        setCurrentUser(userData);
      } catch (err: any) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);

  // Load tasks assigned to the current user
  const loadTasks = async () => {
    try {
      setLoadingTasks(true);
      const response = await api.get('/tasks/my-tasks');
      const tasksData = response.data || [];
      setTasks(tasksData);

      // Check which tasks have reports
      const reportChecks = await Promise.all(
        tasksData.map(async (task: Task) => {
          if (!task.jobId) return { taskId: task.id, hasReport: false };
          try {
            const reportResponse = await api.get(`/jobs/${task.jobId}/tasks/${task.id}/has-report`);
            return { taskId: task.id, hasReport: reportResponse.data.hasReport };
          } catch {
            return { taskId: task.id, hasReport: false };
          }
        })
      );

      const hasReportSet = new Set(
        reportChecks.filter(check => check.hasReport).map(check => check.taskId)
      );
      setTaskHasReport(hasReportSet);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      // Don't show error for tasks, just set empty array
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Load task work logs for selected date
  const loadTaskWorkLogs = async (date?: Date) => {
    if (!currentUserId) return;
    
    try {
      setLoadingTaskWorkLogs(true);
      const dateToLoad = date || selectedDate;
      const dateStr = formatDateToServerTimezone(dateToLoad).split('T')[0]; // Format as YYYY-MM-DD
      const response = await api.get(`/working-log/task-work-logs?user_id=${currentUserId}&date=${dateStr}`);
      setTaskWorkLogs(response.data?.data || []);
    } catch (err: any) {
      console.error('Error loading task work logs:', err);
      // Don't show error, just set empty array
      setTaskWorkLogs([]);
    } finally {
      setLoadingTaskWorkLogs(false);
    }
  };

  useEffect(() => {
    loadData();
    loadTasks();
  }, []);

  // Reload data when selected date changes
  useEffect(() => {
    loadData(selectedDate);
    if (currentUserId) {
      loadTaskWorkLogs(selectedDate);
    }
  }, [selectedDate, currentUserId]);

  // Handle check in
  const handleCheckIn = async () => {
    try {
      setCheckingIn(true);

      // If task list doesn't exist, check default position
      if (tasks.length === 0) {
        // Default position coordinates (40.6180192, -73.9322130)
        const DEFAULT_LATITUDE = 40.6180192;
        const DEFAULT_LONGITUDE = -73.9322130;

        // Get worker's current position
        const workerPosition = await getCurrentPositionCoords();
        
        if (!workerPosition) {
          toast.error('Unable to get your current location. Please enable location permissions and try again.');
          setCheckingIn(false);
          return;
        }

        // Check if locations are within threshold (200 meters default)
        const withinThreshold = areLocationsWithinThreshold(
          workerPosition.latitude,
          workerPosition.longitude,
          DEFAULT_LATITUDE,
          DEFAULT_LONGITUDE,
          200 // 200 meters threshold
        );

        if (!withinThreshold) {
          // Calculate distance for display using Haversine formula
          const distance = calculateDistance(
            workerPosition.latitude,
            workerPosition.longitude,
            DEFAULT_LATITUDE,
            DEFAULT_LONGITUDE
          );
          const distanceFormatted = formatDistance(distance);
          
          toast.error(
            `You are not at the default work location. ` +
            `You are ${distanceFormatted} away from the required location. ` +
            `Please move to the correct location before reporting for work.`
          );
          setCheckingIn(false);
          return;
        }
      }

      const position = await getCurrentPosition();
      
      // Always send position field, even if null
      const payload = { in_position: position || null };
      
      console.log('Sending checkin payload:', payload);

      await api.post('/working-log/checkin', payload);

      // Show info message if position is not available
      if (!position) {
        const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
        if (!isSecure) {
          toast.info('Checked in successfully! Note: Location not recorded (HTTPS required for geolocation).');
        } else {
          toast.info('Checked in successfully! Note: Location permission was denied or unavailable.');
        }
      } else {
        toast.success('Successfully checked in with location!');
      }
      await loadData();
    } catch (err: any) {
      console.error('Error checking in:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to check in';
      toast.error(errorMessage);
    } finally {
      setCheckingIn(false);
    }
  };

  // Handle check out
  const handleCheckOut = async () => {
    try {
      setCheckingOut(true);

      const position = await getCurrentPosition();
      
      // Always send position field, even if null
      const payload = { out_position: position || null };
      
      console.log('Sending checkout payload:', payload);

      await api.post('/working-log/checkout', payload);

      // Show info message if position is not available
      if (!position) {
        const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
        if (!isSecure) {
          toast.info('Checked out successfully! Note: Location not recorded (HTTPS required for geolocation).');
        } else {
          toast.info('Checked out successfully! Note: Location permission was denied or unavailable.');
        }
      } else {
        toast.success('Successfully checked out with location!');
      }
      await loadData();
    } catch (err: any) {
      console.error('Error checking out:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to check out';
      toast.error(errorMessage);
    } finally {
      setCheckingOut(false);
    }
  };


  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    const selected = new Date(selectedDate);
    return (
      today.getFullYear() === selected.getFullYear() &&
      today.getMonth() === selected.getMonth() &&
      today.getDate() === selected.getDate()
    );
  };

  // Calculate duration between two times
  const calculateDuration = (startTime: string | null, endTime: string | null): string => {
    if (!startTime || !endTime) return '-';
    
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      
      // Validate dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.warn('Invalid date in duration calculation:', { startTime, endTime });
        return '-';
      }
      
      let diffMs = end.getTime() - start.getTime();
      
      // If duration is negative, check if end time is on the same day as start time
      // If so, assume end time is on the next day (work spanning midnight)
      if (diffMs < 0) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        
        // Check if they're on the same calendar day
        const sameDay = startDate.getFullYear() === endDate.getFullYear() &&
                       startDate.getMonth() === endDate.getMonth() &&
                       startDate.getDate() === endDate.getDate();
        
        if (sameDay) {
          // End time appears earlier on same day - assume it's next day
          const nextDayEnd = new Date(end);
          nextDayEnd.setDate(nextDayEnd.getDate() + 1);
          diffMs = nextDayEnd.getTime() - start.getTime();
        } else {
          // Different days but still negative - use absolute value as fallback
          diffMs = Math.abs(diffMs);
          console.warn('Negative duration with different days:', { 
            startTime, 
            endTime, 
            startDate: start.toISOString(),
            endDate: end.toISOString() 
          });
        }
      }
      
      // Calculate total minutes (round to nearest minute)
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      
      // Handle zero or very small durations
      if (diffMinutes === 0) {
        return '0m';
      }
      
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      
      if (hours > 0) {
        return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
      }
      return `${minutes}m`;
    } catch (error) {
      console.error('Error calculating duration:', error, { startTime, endTime });
      return '-';
    }
  };

  // Handle starting task work
  const handleStartTask = async (taskId: number) => {
    // Only allow starting tasks for today
    if (!isToday()) {
      toast.error('You can only start tasks for today. Please select today\'s date.');
      return;
    }
    
    // Check if user has checked in today
    if (!todayStatus || !todayStatus.exists || !todayStatus.checkin) {
      toast.error('Please click "START WORK" to start your daily work before starting tasks.');
      return;
    }

    // Find the task to check its work location
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      toast.error('Task not found');
      return;
    }

    // Default position coordinates (40.6180192, -73.9322130)
    const DEFAULT_LATITUDE = 40.6180192;
    const DEFAULT_LONGITUDE = -73.9322130;

    // Determine work location - use task work location if exists, otherwise use default
    let taskLat: number;
    let taskLon: number;
    let isDefaultLocation = false;

    if (task.workLocation && task.workLocation.latitude !== null && task.workLocation.longitude !== null) {
      // Task has a work location, use it
      taskLat = task.workLocation.latitude;
      taskLon = task.workLocation.longitude;
    } else {
      // Task doesn't have a work location, use default position
      taskLat = DEFAULT_LATITUDE;
      taskLon = DEFAULT_LONGITUDE;
      isDefaultLocation = true;
    }

    // Get worker's current position
    const workerPosition = await getCurrentPositionCoords();
    
    if (!workerPosition) {
      toast.error('Unable to get your current location. Please enable location permissions and try again.');
      return;
    }

    // Compare worker position with task work location (or default location)
    const workerLat = workerPosition.latitude;
    const workerLon = workerPosition.longitude;

    // Check if locations are within threshold (200 meters default)
    const withinThreshold = areLocationsWithinThreshold(
      workerLat,
      workerLon,
      taskLat,
      taskLon,
      200 // 200 meters threshold
    );

    if (!withinThreshold) {
      // Calculate distance for display using Haversine formula
      const distance = calculateDistance(workerLat, workerLon, taskLat, taskLon);
      const distanceFormatted = formatDistance(distance);
      
      const locationMessage = isDefaultLocation 
        ? 'the default work location'
        : 'the task work location';
      
      toast.error(
        `You are not at ${locationMessage}. ` +
        `You are ${distanceFormatted} away from the required location. ` +
        `Please move to the correct location before starting the task.`
      );
      return;
    }

    try {
      setStartingTask(taskId);

      await api.post(`/tasks/${taskId}/start`);

      toast.success('Task work started successfully!');
      await loadTasks();
      await loadTaskWorkLogs();
    } catch (err: any) {
      console.error('Error starting task work:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to start task work';
      toast.error(errorMessage);
    } finally {
      setStartingTask(null);
    }
  };

  // Handle stopping task work
  const handleStopTask = async (taskId: number, completed: boolean) => {
    try {
      setStoppingTask(taskId);

      await api.post(`/tasks/${taskId}/stop`, { completed });

      toast.success(completed ? 'Task completed successfully!' : 'Task work stopped successfully!');
      await loadTasks();
      await loadTaskWorkLogs();
    } catch (err: any) {
      console.error('Error stopping task work:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to stop task work';
      toast.error(errorMessage);
    } finally {
      setStoppingTask(null);
      setConfirmDialogOpen(false);
      setTaskToStop(null);
    }
  };

  // Open confirmation dialog for stopping task
  const handleStopClick = (taskId: number) => {
    if (!taskHasReport.has(taskId)) {
      toast.error('Please submit a report before stopping the task.');
      return;
    }
    setTaskToStop(taskId);
    setConfirmDialogOpen(true);
  };

  // Handle report submission
  const handleReportSubmit = async (taskId: number) => {
    // Mark task as having report
    setTaskHasReport(prev => new Set(prev).add(taskId));
    setReportDialogOpen(false);
    setTaskToReport(null);
    toast.success('Report submitted successfully!');
  };

  // Handle task row click - navigate to job detail/product page
  const handleTaskClick = (task: Task & { workLog?: { id: number; startTime: string | null; endTime: string | null } | null }) => {
    if (!task.jobId) {
      toast.error('Task does not have an associated job');
      return;
    }

    // Navigate to job detail with product tab
    // For both field workers and non-field workers, go to the same route
    // The route is /jobs/:jobId#product which shows JobDetail with Product tab
    navigate(`/jobs/${task.jobId}#product`);
  };

  // Handle reload - reload all data
  const handleReload = useCallback(async () => {
    try {
      await Promise.all([
        loadData(selectedDate),
        loadTasks(),
        currentUserId ? loadTaskWorkLogs(selectedDate) : Promise.resolve(),
      ]);
      toast.success('Data reloaded successfully');
    } catch (err: any) {
      console.error('Error reloading data:', err);
      toast.error('Failed to reload data');
    }
  }, [selectedDate, currentUserId]);

  // Listen for task notifications and auto-reload when on working log page
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    const handleNewNotification = (notification: any) => {
      // Check if this is a task notification and we're on the working log page
      const isTaskNotification = notification.notification_type === 'task';
      const isWorkingLogPage = location.pathname === '/clockin';
      
      if (isTaskNotification && isWorkingLogPage) {
        // Automatically reload data when task notification is received
        handleReload();
      }
    };

    socket.on('new-notification', handleNewNotification);

    return () => {
      socket.off('new-notification', handleNewNotification);
    };
  }, [socket, isConnected, location.pathname, handleReload]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 2 : 0 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Tier Items
              </Typography>
              <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 600 }}>
                Working Log
              </Typography>
            </Box>
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <DatePicker
                  label="Work Date"
                  value={selectedDate}
                  onChange={(newValue) => {
                    if (newValue) setSelectedDate(newValue);
                  }}
                  slotProps={{ textField: { size: 'medium' } }}
                  sx={{ minWidth: 200 }}
                />
                {!isToday() && (
                  <Button variant="outlined" onClick={() => setSelectedDate(new Date())} size="medium">
                    Today
                  </Button>
                )}
                <Tooltip title="Reload data">
                  <IconButton onClick={handleReload} disabled={loading} color="primary" size="medium">
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          {isMobile && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DatePicker
                label="Work Date"
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue) setSelectedDate(newValue);
                }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                sx={{ flex: 1 }}
              />
              {!isToday() && (
                <Button variant="outlined" onClick={() => setSelectedDate(new Date())} size="small">
                  Today
                </Button>
              )}
              <Tooltip title="Reload data">
                <IconButton onClick={handleReload} disabled={loading} color="primary" size="small">
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

      {/* Status Card - Show all clock in/out times */}
      {todayStatus && todayStatus.exists && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {isToday() ? "Today's Status" : `Status for ${selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
            </Typography>
            {todayStatus.entries && todayStatus.entries.length > 0 ? (
              isMobile ? (
                <Stack spacing={1}>
                  {todayStatus.entries.map((entry) => (
                    <Paper key={entry.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Clock In</Typography>
                          <Typography variant="body1" color="primary" fontWeight={500}>
                            {entry.checkin ? formatTime(entry.checkin) : '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">Clock Out</Typography>
                          {entry.checkout ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Typography variant="body1" color="secondary" fontWeight={500}>
                                {formatTime(entry.checkout)}
                              </Typography>
                              {!entry.out_position && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>(Auto)</Typography>
                              )}
                            </Box>
                          ) : (
                            <Box><Chip label="In Progress" color="warning" size="small" /></Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">Duration</Typography>
                          <Typography variant="body2">
                            {entry.checkin && entry.checkout
                              ? calculateDuration(entry.checkin, entry.checkout)
                              : entry.checkin ? 'Ongoing...' : '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Clock In Time</strong></TableCell>
                      <TableCell><strong>Clock Out Time</strong></TableCell>
                      <TableCell><strong>Duration</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayStatus.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {entry.checkin ? (
                            <Typography variant="body1" color="primary">
                              {formatTime(entry.checkin)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.checkout ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" color="secondary">
                                {formatTime(entry.checkout)}
                              </Typography>
                              {!entry.out_position && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  (Auto)
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              <Chip label="In Progress" color="warning" size="small" />
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.checkin && entry.checkout ? (
                            <Typography variant="body2">
                              {calculateDuration(entry.checkin, entry.checkout)}
                            </Typography>
                          ) : entry.checkin ? (
                            <Typography variant="body2" color="text.secondary">
                              Ongoing...
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )
            ) : (
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {todayStatus.checkin && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Clock In Time
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatTime(todayStatus.checkin)}
                    </Typography>
                  </Box>
                )}
                {todayStatus.checkout && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Clock Out Time
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" color="secondary">
                        {formatTime(todayStatus.checkout)}
                      </Typography>
                      {!todayStatus.out_position && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          (Automatically recorded)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Task List Section - Only show if tasks are assigned and work date is today */}
      {tasks.length > 0 && isToday() && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              My Tasks
            </Typography>
            
            {/* START WORK and STOP WORK Buttons - Only show for today */}
            {isToday() && (
              <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<Login />}
                  onClick={handleCheckIn}
                  disabled={!canCheckIn || checkingIn || checkingOut}
                  fullWidth={isMobile}
                  sx={isMobile ? undefined : { minWidth: 200 }}
                >
                  {checkingIn ? <CircularProgress size={24} /> : 'START WORK'}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<Logout />}
                  onClick={handleCheckOut}
                  disabled={!canCheckOut || checkingIn || checkingOut}
                  fullWidth={isMobile}
                  sx={isMobile ? undefined : { minWidth: 200 }}
                >
                  {checkingOut ? <CircularProgress size={24} /> : 'STOP WORK'}
                </Button>
              </Stack>
            )}

            {/* Info Alert if user hasn't checked in - Only show for today */}
            {isToday() && (!todayStatus || !todayStatus.exists || !todayStatus.checkin) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please click "START WORK" to start your daily work before you can start any tasks.
              </Alert>
            )}

            {loadingTasks ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {tasks.map((task) => {
                  const workLog = task.workLog;
                  const hasStarted = workLog?.startTime !== null && workLog?.startTime !== undefined;
                  const hasStopped = workLog?.endTime !== null && workLog?.endTime !== undefined;

                  return (
                    <Paper
                      key={task.id}
                      variant="outlined"
                      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleTaskClick(task)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>{task.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[task.jobNumber, task.roomName, task.taskType].filter(Boolean).join(' · ') || '-'}
                          </Typography>
                        </Box>
                        <Chip
                          label={task.status}
                          color={
                            task.status === 'DONE' ? 'success' :
                            task.status === 'IN PROGRESS' ? 'primary' :
                            task.status === 'COMPLETE' ? 'info' :
                            'default'
                          }
                          size="small"
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Start</Typography>
                            <Typography variant="body2">{workLog?.startTime ? formatTime(workLog.startTime) : '-'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">End</Typography>
                            <Typography variant="body2">{workLog?.endTime ? formatTime(workLog.endTime) : '-'}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          {!hasStarted ? (
                            <Tooltip
                              title={
                                !isToday()
                                  ? "You can only start tasks for today"
                                  : !todayStatus || !todayStatus.exists || !todayStatus.checkin
                                  ? "Please click 'START WORK' first"
                                  : "Start task work"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleStartTask(task.id)}
                                  disabled={
                                    startingTask === task.id ||
                                    !isToday() ||
                                    !todayStatus ||
                                    !todayStatus.exists ||
                                    !todayStatus.checkin
                                  }
                                >
                                  {startingTask === task.id ? <CircularProgress size={20} /> : <PlayArrow />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : !hasStopped ? (
                            <>
                              <Tooltip title="Report work result">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTaskToReport(task);
                                    setReportDialogOpen(true);
                                  }}
                                >
                                  <Description />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={taskHasReport.has(task.id) ? "Stop task work" : "Submit a report before stopping"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleStopClick(task.id)}
                                    disabled={stoppingTask === task.id || !taskHasReport.has(task.id)}
                                  >
                                    {stoppingTask === task.id ? <CircularProgress size={20} /> : <Stop />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : null}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Job</strong></TableCell>
                      <TableCell><strong>Room</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Start Time</strong></TableCell>
                      <TableCell><strong>End Time</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.map((task) => {
                      const workLog = task.workLog;
                      const hasStarted = workLog?.startTime !== null && workLog?.startTime !== undefined;
                      const hasStopped = workLog?.endTime !== null && workLog?.endTime !== undefined;

                      return (
                        <TableRow
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            }
                          }}
                        >
                          <TableCell>{task.title}</TableCell>
                          <TableCell>{task.jobNumber || '-'}</TableCell>
                          <TableCell>{task.roomName || '-'}</TableCell>
                          <TableCell>{task.taskType || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={task.status}
                              color={
                                task.status === 'DONE' ? 'success' :
                                task.status === 'IN PROGRESS' ? 'primary' :
                                task.status === 'COMPLETE' ? 'info' :
                                'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {workLog?.startTime ? formatTime(workLog.startTime) : '-'}
                          </TableCell>
                          <TableCell>
                            {workLog?.endTime ? formatTime(workLog.endTime) : '-'}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {!hasStarted ? (
                                <Tooltip
                                  title={
                                    !isToday()
                                      ? "You can only start tasks for today"
                                      : !todayStatus || !todayStatus.exists || !todayStatus.checkin
                                      ? "Please click 'START WORK' to start your daily work before starting tasks"
                                      : "Start task work"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStartTask(task.id)}
                                      disabled={
                                        startingTask === task.id ||
                                        !isToday() ||
                                        !todayStatus ||
                                        !todayStatus.exists ||
                                        !todayStatus.checkin
                                      }
                                    >
                                      {startingTask === task.id ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <PlayArrow />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : !hasStopped ? (
                                <>
                                  <Tooltip title="Report work result">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTaskToReport(task);
                                        setReportDialogOpen(true);
                                      }}
                                    >
                                      <Description />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={taskHasReport.has(task.id) ? "Stop task work" : "Please submit a report before stopping"}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={() => handleStopClick(task.id)}
                                        disabled={stoppingTask === task.id || !taskHasReport.has(task.id)}
                                      >
                                        {stoppingTask === task.id ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <Stop />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Task Time List Section - Show all task work logs for selected date */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Task Time List
            {!isToday() && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
              </Typography>
            )}
          </Typography>
          
          {loadingTaskWorkLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : taskWorkLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No task work logs found for this date.
            </Typography>
          ) : isMobile ? (
            <Stack spacing={1}>
              {taskWorkLogs.map((log) => (
                <Paper key={log.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{log.task_title || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{log.task_type || '-'}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Start</Typography>
                      <Typography variant="body2">{log.start_time ? formatTime(log.start_time) : '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">End</Typography>
                      <Typography variant="body2">{log.end_time ? formatTime(log.end_time) : '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="body2">{calculateDuration(log.start_time, log.end_time)}</Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
              {taskWorkLogs.some(log => log.start_time && log.end_time) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Total Time: {(() => {
                      const totalMinutes = taskWorkLogs.reduce((total, log) => {
                        if (!log.start_time || !log.end_time) return total;
                        try {
                          const start = new Date(log.start_time);
                          const end = new Date(log.end_time);
                          if (isNaN(start.getTime()) || isNaN(end.getTime())) return total;
                          let diffMs = end.getTime() - start.getTime();
                          if (diffMs < 0) diffMs = Math.abs(diffMs);
                          return total + Math.round(diffMs / (1000 * 60));
                        } catch { return total; }
                      }, 0);
                      if (totalMinutes === 0) return '0m';
                      const hours = Math.floor(totalMinutes / 60);
                      const minutes = totalMinutes % 60;
                      return hours > 0 ? `${hours}h ${minutes > 0 ? `${minutes}m` : ''}` : `${minutes}m`;
                    })()}
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Task Title</strong></TableCell>
                    <TableCell><strong>Task Type</strong></TableCell>
                    <TableCell><strong>Start Time</strong></TableCell>
                    <TableCell><strong>End Time</strong></TableCell>
                    <TableCell><strong>Duration</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskWorkLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.task_title || '-'}</TableCell>
                      <TableCell>{log.task_type || '-'}</TableCell>
                      <TableCell>{log.start_time ? formatTime(log.start_time) : '-'}</TableCell>
                      <TableCell>{log.end_time ? formatTime(log.end_time) : '-'}</TableCell>
                      <TableCell>{calculateDuration(log.start_time, log.end_time)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Total row */}
                  {taskWorkLogs.some(log => log.start_time && log.end_time) && (
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell colSpan={4} align="right">
                        <Typography variant="body2" fontWeight="bold">
                          Total Time:
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {(() => {
                            const totalMinutes = taskWorkLogs.reduce((total, log) => {
                              if (!log.start_time || !log.end_time) return total;
                              try {
                                const start = new Date(log.start_time);
                                const end = new Date(log.end_time);
                                
                                // Validate dates
                                if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                                  return total;
                                }
                                
                                let diffMs = end.getTime() - start.getTime();
                                
                                // If duration is negative, check if end time is on the same day as start time
                                // If so, assume end time is on the next day (work spanning midnight)
                                if (diffMs < 0) {
                                  const startDate = new Date(start);
                                  const endDate = new Date(end);
                                  
                                  // Check if they're on the same calendar day
                                  const sameDay = startDate.getFullYear() === endDate.getFullYear() &&
                                                 startDate.getMonth() === endDate.getMonth() &&
                                                 startDate.getDate() === endDate.getDate();
                                  
                                  if (sameDay) {
                                    // End time appears earlier on same day - assume it's next day
                                    const nextDayEnd = new Date(end);
                                    nextDayEnd.setDate(nextDayEnd.getDate() + 1);
                                    diffMs = nextDayEnd.getTime() - start.getTime();
                                  } else {
                                    // Different days but still negative - use absolute value as fallback
                                    diffMs = Math.abs(diffMs);
                                  }
                                }
                                
                                // Round to nearest minute
                                const diffMinutes = Math.round(diffMs / (1000 * 60));
                                return total + diffMinutes;
                              } catch {
                                return total;
                              }
                            }, 0);
                            
                            if (totalMinutes === 0) {
                              return '0m';
                            }
                            
                            const hours = Math.floor(totalMinutes / 60);
                            const minutes = totalMinutes % 60;
                            
                            if (hours > 0) {
                              return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
                            }
                            return `${minutes}m`;
                          })()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Start Task Section - Show when no tasks assigned - Only show for today */}
      {tasks.length === 0 && !loadingTasks && isToday() && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Start Task
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No tasks are currently assigned to you. You can start working on tasks directly.
            </Typography>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Login />}
                onClick={handleCheckIn}
                disabled={!canCheckIn || checkingIn || checkingOut}
                fullWidth={isMobile}
                sx={isMobile ? undefined : { minWidth: 200 }}
              >
                {checkingIn ? <CircularProgress size={24} /> : 'START WORK'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<Logout />}
                onClick={handleCheckOut}
                disabled={!canCheckOut || checkingIn || checkingOut}
                fullWidth={isMobile}
                sx={isMobile ? undefined : { minWidth: 200 }}
              >
                {checkingOut ? <CircularProgress size={24} /> : 'STOP WORK'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog for Stopping Task */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setTaskToStop(null);
        }}
      >
        <DialogTitle>Stop Task Work</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {taskToStop && tasks.find(t => t.id === taskToStop)?.teamLeaderId === currentUserId
              ? 'Have you completed the task?'
              : 'Stop task work?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setTaskToStop(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          {taskToStop && tasks.find(t => t.id === taskToStop)?.teamLeaderId === currentUserId ? (
            <>
              <Button
                onClick={() => {
                  if (taskToStop) {
                    handleStopTask(taskToStop, false);
                  }
                }}
                color="primary"
                variant="outlined"
              >
                No, Just Stop
              </Button>
              <Button
                onClick={() => {
                  if (taskToStop) {
                    handleStopTask(taskToStop, true);
                  }
                }}
                color="primary"
                variant="contained"
              >
                Yes, Completed
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                if (taskToStop) {
                  handleStopTask(taskToStop, false);
                }
              }}
              color="primary"
              variant="contained"
            >
              Stop
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Task Report Dialog */}
      <TaskReportDialog
        open={reportDialogOpen}
        onClose={() => {
          setReportDialogOpen(false);
          setTaskToReport(null);
        }}
        task={taskToReport}
        onReportSubmit={handleReportSubmit}
      />

      </Box>
    </LocalizationProvider>
  );
}
